import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { MembersPanel } from '../components/MembersPanel.js';
import { Panel } from '../components/Panel.js';
import {
  Button,
  Card,
  Field,
  Icon,
  type IconName,
  PageHeader,
  Select,
  Switch,
  Tabs,
  TextInput,
  ThemeSelect,
} from '../components/ui/index.js';
import { readJson, useRemote } from '../lib/remote.js';
import { useAdminLocale } from '../lib/admin-locale.js';
import { useSession } from '../lib/session.js';
import { timezoneChoices } from '../lib/timezones.js';
import { useUiPrefs } from '../lib/ui-prefs.js';

const TABS = [
  { id: 'general', el: 'Γενικά', en: 'General', icon: 'settings' },
  { id: 'users', el: 'Χρήστες και ρόλοι', en: 'Users and roles', icon: 'users' },
  { id: 'languages', el: 'Γλώσσες', en: 'Languages', icon: 'globe' },
  { id: 'notifications', el: 'Ειδοποιήσεις', en: 'Notifications', icon: 'mail' },
  { id: 'privacy', el: 'Απόρρητο', en: 'Privacy', icon: 'shield' },
] as const satisfies ReadonlyArray<{ id: string; el: string; en: string; icon: IconName }>;

type TabId = (typeof TABS)[number]['id'];

const LOCALES = [
  { code: 'el', el: 'Ελληνικά', en: 'Greek' },
  { code: 'en', el: 'English', en: 'English' },
  { code: 'fr', el: 'Français', en: 'French' },
] as const;

type LocaleCode = (typeof LOCALES)[number]['code'];

const LOCALE_COPY: Record<LocaleCode, { greeting: string; fallback: string }> = {
  el: {
    greeting: 'Γεια σας. Πώς μπορώ να βοηθήσω;',
    fallback: 'Δεν έχω αρκετές πληροφορίες για να απαντήσω.',
  },
  en: {
    greeting: 'Hello. How can I help?',
    fallback: 'I do not have enough information to answer.',
  },
  fr: {
    greeting: 'Bonjour. Comment puis-je vous aider ?',
    fallback: "Je n'ai pas assez d'informations pour répondre.",
  },
};

function isLocaleCode(value: string): value is LocaleCode {
  return LOCALES.some((item) => item.code === value);
}

const ROLE_LABEL: Record<string, readonly [el: string, en: string]> = {
  TENANT_ADMIN: ['Διαχειριστής', 'Administrator'],
  TENANT_VIEWER: ['Προβολή', 'Viewer'],
  PLATFORM_ADMIN: ['Διαχειριστής πλατφόρμας', 'Platform administrator'],
};

interface Organization {
  id: string;
  name: string;
  status: string;
  timezone: string;
}

interface SettingsPayload {
  organization: Organization;
}

interface SiteRow {
  id: string;
  name: string;
  canonicalOrigin: string;
}

interface BotLocale {
  locale: string;
  enabled: boolean;
  greeting: string;
  fallback: string;
}

interface BotData {
  configVersion: number;
  defaultLocale: string;
  locales: BotLocale[];
  settings: {
    privacyUrl: string | null;
    contactUrl: string | null;
    leadNotifyEmail: string | null;
    leadNotifyEmailPending?: string | null;
    noticeVersion: string;
    historyRetentionDays: number;
  };
}

function hostOf(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
}

function isTab(value: string | null): value is TabId {
  return TABS.some((tab) => tab.id === value);
}

function Notice(props: { tone: 'ok' | 'error'; text: string }) {
  return (
    <p
      className={
        props.tone === 'ok'
          ? 'text-sm text-emerald-700 dark:text-emerald-300'
          : 'text-sm text-rose-700 dark:text-rose-300'
      }
    >
      {props.text}
    </p>
  );
}

function SiteSelect(props: { sites: SiteRow[]; value: string; onChange: (id: string) => void }) {
  const { text } = useAdminLocale();
  return (
    <Field label={text('Ιστοσελίδα', 'Website')}>
      <Select
        className="mt-1.5"
        value={props.value}
        onChange={props.onChange}
        options={props.sites.map((site) => ({
          value: site.id,
          label: `${hostOf(site.canonicalOrigin)} — ${site.name}`,
        }))}
      />
    </Field>
  );
}

export function SettingsPage(props: { canWrite: boolean; role: string | null; platform: boolean }) {
  const { text } = useAdminLocale();
  const { user, changePassword } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab: TabId = isTab(requested) ? requested : 'general';

  useEffect(() => {
    const token = params.get('leadVerify');
    if (!token) return;
    let cancelled = false;
    void readJson('/v1/admin-ui/lead-notify/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
      .then(() => {
        if (cancelled) return;
        const next = new URLSearchParams(params);
        next.delete('leadVerify');
        next.set('tab', 'notifications');
        setParams(next, { replace: true });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [params, setParams]);

  useEffect(() => {
    if (requested === 'provider' && props.platform) {
      void navigate('/platform/settings', { replace: true });
    }
  }, [navigate, props.platform, requested]);
  const settings = useRemote<SettingsPayload>('/v1/settings');
  const sites = useRemote<SiteRow[]>('/v1/sites');
  const [siteId, setSiteId] = useState<string | null>(null);
  const siteList = sites.data ?? [];
  const chosenSite = siteId ?? siteList[0]?.id ?? null;
  const needsSite = tab === 'languages' || tab === 'notifications' || tab === 'privacy';
  const bot = useRemote<BotData>(needsSite && chosenSite ? `/v1/sites/${chosenSite}/bot` : null);

  function openTab(next: TabId): void {
    const copy = new URLSearchParams(params);
    if (next === 'general') copy.delete('tab');
    else copy.set('tab', next);
    setParams(copy, { replace: true });
  }

  return (
    <section className="space-y-6">
      <PageHeader
        description={
          props.platform
            ? text(
                'Στοιχεία, χρήστες, γλώσσες, ειδοποιήσεις και απόρρητο του επιλεγμένου πελάτη. Ο πάροχος AI είναι κοινός και αλλάζει από τις ρυθμίσεις πλατφόρμας.',
                'Details, users, languages, notifications, and privacy for the selected client. The AI provider is shared and changed in platform settings.',
              )
            : text(
                'Στοιχεία οργανισμού, χρήστες, γλώσσες, ειδοποιήσεις και απόρρητο. Ο πάροχος AI ορίζεται από την πλατφόρμα. Η εμφάνιση κάθε chatbot μένει στη διαχείριση της ιστοσελίδας.',
                'Organization details, users, languages, notifications, and privacy. The AI provider is set by the platform. Each chatbot’s appearance remains in website management.',
              )
        }
      />
      {props.platform ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
          <Link
            to="/platform/settings"
            className="font-medium text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200"
          >
            {text('Ρυθμίσεις πλατφόρμας', 'Platform settings')}
          </Link>
        </p>
      ) : null}
      <Tabs
        className="gap-1 overflow-x-auto"
        items={TABS.map((item) => ({
          id: item.id,
          label: text(item.el, item.en),
          icon: item.icon,
        }))}
        value={tab}
        onChange={(id) => openTab(id as (typeof TABS)[number]['id'])}
      />

      {tab === 'general' ? (
        <GeneralTab
          canWrite={props.canWrite}
          role={props.role}
          email={user?.email ?? ''}
          settings={settings.data ?? null}
          loading={!settings.data && !settings.error}
          error={settings.error?.message ?? null}
          onSaved={() => void settings.mutate()}
          onPassword={changePassword}
        />
      ) : null}

      {tab === 'users' ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
            {text(
              'Ο ρόλος επιλέγεται από τη λίστα. Διαχειριστής αλλάζει ρυθμίσεις. Προβολή μόνο διαβάζει.',
              'Select a role from the list. Administrators can change settings; viewers have read-only access.',
            )}
          </p>
          <MembersPanel canWrite={props.canWrite} currentUserId={user?.id} />
        </div>
      ) : null}

      {needsSite ? (
        <Panel
          loading={!sites.data && !sites.error}
          error={sites.error?.message ?? null}
          empty={siteList.length === 0}
          emptyLabel={text('Δεν υπάρχει ιστοσελίδα.', 'No website found.')}
        >
          {chosenSite ? (
            <div className="space-y-4">
              {tab !== 'languages' ? (
                <Card>
                  <SiteSelect sites={siteList} value={chosenSite} onChange={setSiteId} />
                </Card>
              ) : null}
              {bot.error ? <Notice tone="error" text={bot.error.message} /> : null}
              {!bot.data && !bot.error ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {text('Φόρτωση…', 'Loading…')}
                </p>
              ) : null}
              {bot.data && tab === 'languages' ? (
                <LanguageEditor
                  key={`${chosenSite}:${bot.data.configVersion}`}
                  siteId={chosenSite}
                  sites={siteList}
                  onSiteChange={setSiteId}
                  bot={bot.data}
                  canWrite={props.canWrite}
                  onSaved={() => void bot.mutate()}
                />
              ) : null}
              {bot.data && tab === 'notifications' ? (
                <NotificationEditor
                  key={`${chosenSite}:${bot.data.configVersion}`}
                  siteId={chosenSite}
                  bot={bot.data}
                  canWrite={props.canWrite}
                  onSaved={() => void bot.mutate()}
                />
              ) : null}
              {bot.data && tab === 'privacy' ? (
                <PrivacyEditor
                  key={`${chosenSite}:${bot.data.configVersion}`}
                  siteId={chosenSite}
                  bot={bot.data}
                  canWrite={props.canWrite}
                  onSaved={() => void bot.mutate()}
                />
              ) : null}
            </div>
          ) : null}
        </Panel>
      ) : null}
    </section>
  );
}

function OrganizationForm(props: {
  organization: Organization;
  canWrite: boolean;
  onSaved: () => void;
}) {
  const { locale, text } = useAdminLocale();
  const zones = timezoneChoices(props.organization.timezone);
  const [name, setName] = useState(props.organization.name);
  const [timezone, setTimezone] = useState(props.organization.timezone);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [pending, setPending] = useState(false);

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setNotice(null);
    try {
      await readJson('/v1/settings/organization', {
        method: 'PATCH',
        body: JSON.stringify({ name: name.trim(), timezone }),
      });
      setNotice({ tone: 'ok', text: text('Αποθηκεύτηκε.', 'Saved.') });
      props.onSaved();
    } catch (error) {
      setNotice({
        tone: 'error',
        text: error instanceof Error ? error.message : text('Η αποθήκευση απέτυχε', 'Save failed'),
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={(event) => void save(event)} className="grid gap-4 sm:grid-cols-2">
      <Field label={text('Όνομα οργανισμού', 'Organization name')}>
        <TextInput
          required
          value={name}
          disabled={!props.canWrite}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <Field label={text('Ζώνη ώρας', 'Time zone')}>
        <Select
          className="mt-1.5"
          value={timezone}
          disabled={!props.canWrite}
          searchable
          onChange={setTimezone}
          options={zones.map((zone) => ({ value: zone, label: zone }))}
        />
      </Field>
      <Field
        label={text('Γλώσσα περιβάλλοντος', 'Interface language')}
        hint={text(
          'Η γλώσσα διαχείρισης επιλέγεται από τον διακόπτη γλώσσας.',
          'The admin language is selected with the language switcher.',
        )}
      >
        <Select
          className="mt-1.5"
          value={locale}
          disabled
          onChange={() => undefined}
          options={[
            {
              value: locale,
              label: locale === 'en' ? 'English' : 'Ελληνικά',
            },
          ]}
        />
      </Field>
      <Field
        label={text('Θέμα εμφάνισης', 'Appearance theme')}
        hint={text('Ισχύει μόνο σε αυτόν τον περιηγητή.', 'Applies only in this browser.')}
      >
        <ThemeSelect />
      </Field>
      {props.canWrite ? (
        <div className="flex items-end">
          <Button type="submit" disabled={pending}>
            <Icon name="save" className="size-4" />
            {pending ? text('Αποθήκευση…', 'Saving…') : text('Αποθήκευση', 'Save')}
          </Button>
        </div>
      ) : null}
      {notice ? (
        <div className="sm:col-span-2">
          <Notice tone={notice.tone} text={notice.text} />
        </div>
      ) : null}
    </form>
  );
}

function GeneralTab(props: {
  canWrite: boolean;
  role: string | null;
  email: string;
  settings: SettingsPayload | null;
  loading: boolean;
  error: string | null;
  onSaved: () => void;
  onPassword: (currentPassword: string, newPassword: string) => Promise<string | null>;
}) {
  const { text } = useAdminLocale();
  const organization = props.settings?.organization;
  const roleLabel = props.role ? ROLE_LABEL[props.role] : undefined;
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordNotice, setPasswordNotice] = useState<{
    tone: 'ok' | 'error';
    text: string;
  } | null>(null);

  async function savePassword(event: FormEvent): Promise<void> {
    event.preventDefault();
    const message = await props.onPassword(currentPassword, newPassword);
    if (message) {
      setPasswordNotice({ tone: 'error', text: message });
      return;
    }
    setCurrentPassword('');
    setNewPassword('');
    setPasswordNotice({ tone: 'ok', text: text('Ο κωδικός άλλαξε.', 'Password changed.') });
  }

  return (
    <Panel
      loading={props.loading}
      error={props.error}
      empty={!organization}
      emptyLabel={text('Δεν βρέθηκε ο οργανισμός.', 'Organization not found.')}
    >
      <div className="grid gap-4 lg:grid-cols-3">
        <Card
          className="lg:col-span-2"
          title={text('Στοιχεία οργανισμού', 'Organization details')}
          description={text(
            'Το όνομα και η ζώνη ώρας ανήκουν στον επιλεγμένο οργανισμό. Η ζώνη ορίζει τον μήνα της χρήσης.',
            'The name and time zone belong to the selected organization. The time zone defines the usage month.',
          )}
        >
          {organization ? (
            <OrganizationForm
              key={organization.id}
              organization={organization}
              canWrite={props.canWrite}
              onSaved={props.onSaved}
            />
          ) : null}
        </Card>
        <Card title={text('Λογαριασμός', 'Account')}>
          <div className="flex items-center gap-3">
            <span className="grid size-11 place-items-center rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600">
              <Icon name="users" className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                {props.email}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                {props.role ? (roleLabel ? text(roleLabel[0], roleLabel[1]) : props.role) : '—'}
              </p>
            </div>
          </div>
          <form onSubmit={(event) => void savePassword(event)} className="mt-4 space-y-3">
            <Field label={text('Τρέχων κωδικός', 'Current password')}>
              <TextInput
                type="password"
                required
                autoComplete="current-password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </Field>
            <Field
              label={text('Νέος κωδικός', 'New password')}
              hint={text(
                'Τουλάχιστον 12 χαρακτήρες, όχι ίδιος με το email.',
                'At least 12 characters and different from the email address.',
              )}
            >
              <TextInput
                type="password"
                required
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </Field>
            <Button type="submit" variant="secondary">
              <Icon name="key" className="size-4" />
              {text('Αλλαγή κωδικού', 'Change password')}
            </Button>
            {passwordNotice ? (
              <Notice tone={passwordNotice.tone} text={passwordNotice.text} />
            ) : null}
          </form>
        </Card>
      </div>
    </Panel>
  );
}

function LanguageEditor(props: {
  siteId: string;
  sites: SiteRow[];
  onSiteChange: (id: string) => void;
  bot: BotData;
  canWrite: boolean;
  onSaved: () => void;
}) {
  const { text } = useAdminLocale();
  const ready = new Map(props.bot.locales.map((row) => [row.locale, row]));
  const enabledCodes = LOCALES.filter((item) => {
    const row = ready.get(item.code);
    return row?.enabled === true || item.code === props.bot.defaultLocale;
  }).map((item) => item.code);
  const [defaultLocale, setDefaultLocale] = useState(props.bot.defaultLocale);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState<LocaleCode | 'default' | null>(null);

  async function patch(body: { defaultLocale?: LocaleCode; locales?: BotLocale[] }): Promise<void> {
    await readJson(`/v1/sites/${props.siteId}/bot`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    props.onSaved();
  }

  async function setEnabled(code: LocaleCode, enabled: boolean): Promise<void> {
    if (!props.canWrite) return;
    if (!enabled && (code === defaultLocale || enabledCodes.length < 2)) return;
    const existing = ready.get(code);
    const copy = LOCALE_COPY[code];
    setBusy(code);
    setNotice(null);
    try {
      await patch({
        locales: [
          {
            locale: code,
            enabled,
            greeting: existing?.greeting.trim() || copy.greeting,
            fallback: existing?.fallback.trim() || copy.fallback,
          },
        ],
      });
    } catch (error) {
      setNotice({
        tone: 'error',
        text: error instanceof Error ? error.message : text('Η ενημέρωση απέτυχε', 'Update failed'),
      });
    } finally {
      setBusy(null);
    }
  }

  async function changeDefault(code: string): Promise<void> {
    if (!props.canWrite || !isLocaleCode(code) || code === defaultLocale) return;
    if (!enabledCodes.includes(code)) return;
    setDefaultLocale(code);
    setBusy('default');
    setNotice(null);
    try {
      await patch({ defaultLocale: code });
    } catch (error) {
      setDefaultLocale(props.bot.defaultLocale);
      setNotice({
        tone: 'error',
        text: error instanceof Error ? error.message : text('Η αποθήκευση απέτυχε', 'Save failed'),
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      <Card
        title={text('Γλώσσες chatbot', 'Chatbot languages')}
        description={text(
          'Ενεργοποιήστε τις γλώσσες του widget για αυτή την ιστοσελίδα. Χαιρετισμός και εφεδρικό κείμενο επεξεργάζονται στη διαχείριση της ιστοσελίδας.',
          'Enable widget languages for this website. The greeting and fallback text are edited in website management.',
        )}
        actions={
          <div className="w-full min-w-0 sm:w-64">
            <Select
              aria-label={text('Ιστοσελίδα', 'Website')}
              value={props.siteId}
              onChange={props.onSiteChange}
              options={props.sites.map((site) => ({
                value: site.id,
                label: `${hostOf(site.canonicalOrigin)} — ${site.name}`,
              }))}
            />
          </div>
        }
      >
        <div className="space-y-5">
          <div className="grid gap-3 md:grid-cols-3">
            {LOCALES.map((item) => {
              const row = ready.get(item.code);
              const on = enabledCodes.includes(item.code);
              const isDefault = item.code === defaultLocale;
              const copy = LOCALE_COPY[item.code];
              const greeting = row?.greeting.trim() || copy.greeting;
              const canDisable = on && !isDefault && enabledCodes.length > 1;
              return (
                <article
                  key={item.code}
                  className={[
                    'flex flex-col rounded-xl border p-4 transition',
                    on
                      ? 'border-blue-200 bg-blue-50/40 dark:border-blue-900 dark:bg-blue-950/30'
                      : 'border-slate-200 bg-slate-50/50 dark:border-slate-700 dark:bg-slate-950/40',
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="grid size-9 place-items-center rounded-lg bg-white text-xs font-bold tracking-wide text-slate-700 uppercase shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
                          {item.code}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {text(item.el, item.en)}
                          </p>
                          {isDefault ? (
                            <span className="mt-0.5 inline-flex text-xs font-medium text-emerald-700 dark:text-emerald-300">
                              {text('Προεπιλογή', 'Default')}
                            </span>
                          ) : (
                            <span className="mt-0.5 inline-flex text-xs text-slate-500 dark:text-slate-400">
                              {on ? text('Ενεργή', 'Active') : text('Ανενεργή', 'Inactive')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={on}
                      disabled={!props.canWrite || busy !== null || (on && !canDisable)}
                      aria-label={text(`${item.el} ενεργή`, `${item.en} active`)}
                      onChange={(event) => void setEnabled(item.code, event.target.checked)}
                    />
                  </div>
                  <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                    «{greeting}»
                  </p>
                  {props.canWrite && on && !isDefault ? (
                    <button
                      type="button"
                      disabled={busy !== null}
                      className="mt-3 self-start text-xs font-medium text-blue-700 hover:text-blue-800 disabled:opacity-50 dark:text-blue-300 dark:hover:text-blue-200"
                      onClick={() => void changeDefault(item.code)}
                    >
                      {text('Ορισμός ως προεπιλογή', 'Set as default')}
                    </button>
                  ) : (
                    <span className="mt-3 h-4" />
                  )}
                </article>
              );
            })}
          </div>

          <div className="admin-surface p-4">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {text('Προεπιλεγμένη γλώσσα', 'Default language')}
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {text(
                'Χρησιμοποιείται όταν ο επισκέπτης δεν έχει επιλέξει γλώσσα.',
                'Used when the visitor has not selected a language.',
              )}
            </p>
            <div className="mt-3">
              <Tabs
                variant="pills"
                value={
                  enabledCodes.includes(defaultLocale as LocaleCode)
                    ? defaultLocale
                    : (enabledCodes[0] ?? '')
                }
                onChange={(id) => void changeDefault(id)}
                items={LOCALES.filter((item) => enabledCodes.includes(item.code)).map((item) => ({
                  id: item.code,
                  label: text(item.el, item.en),
                  icon: 'globe' as const,
                }))}
              />
            </div>
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400">
            {text(
              'Διατίθενται ελληνικά, αγγλικά και γαλλικά. Δεν μπορείτε να απενεργοποιήσετε την προεπιλογή ή την τελευταία ενεργή γλώσσα.',
              'Greek, English, and French are available. You cannot disable the default or the last active language.',
            )}
          </p>
          {notice ? <Notice tone={notice.tone} text={notice.text} /> : null}
        </div>
      </Card>
    </div>
  );
}

function NotificationEditor(props: {
  siteId: string;
  bot: BotData;
  canWrite: boolean;
  onSaved: () => void;
}) {
  const { text } = useAdminLocale();
  const [email, setEmail] = useState(props.bot.settings.leadNotifyEmail ?? '');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [pending, setPending] = useState(false);

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setNotice(null);
    try {
      await readJson(`/v1/sites/${props.siteId}/bot`, {
        method: 'PATCH',
        body: JSON.stringify({
          settings: { leadNotifyEmail: email.trim() || null },
        }),
      });
      setNotice({
        tone: 'ok',
        text: email.trim()
          ? text(
              'Αν η διεύθυνση είναι νέα, στάλθηκε σύνδεσμος επιβεβαίωσης.',
              'If this is a new address, a confirmation link was sent.',
            )
          : text('Ο παραλήπτης αφαιρέθηκε.', 'The recipient was removed.'),
      });
      props.onSaved();
    } catch (error) {
      setNotice({
        tone: 'error',
        text: error instanceof Error ? error.message : text('Η αποθήκευση απέτυχε', 'Save failed'),
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <Card
      title={text('Ειδοποιήσεις', 'Notifications')}
      description={text(
        'Νέα επαφή, αποτυχία αποστολής της, συγχρονισμός και όρια στέλνουν email μόνο όταν το αντίστοιχο πρότυπο είναι ενεργό.',
        'New contacts, delivery failures, synchronization, and limits send email only when the corresponding template is enabled.',
      )}
    >
      <form onSubmit={(event) => void save(event)} className="space-y-4">
        <Field
          label={text('Email για νέα επαφή', 'New contact email')}
          hint={text(
            'Κενό αφαιρεί τον παραλήπτη. Νέα διεύθυνση ενεργοποιείται αφού ανοίξει τον σύνδεσμο επιβεβαίωσης.',
            'Leave blank to remove the recipient. A new address is enabled after opening the confirmation link.',
          )}
        >
          <TextInput
            type="email"
            value={email}
            disabled={!props.canWrite}
            onChange={(event) => setEmail(event.target.value)}

            placeholder="owner@example.com"
          />
        </Field>
        {props.bot.settings.leadNotifyEmailPending ? (
          <p className="text-sm text-amber-800 dark:text-amber-200">
            {text('Εκκρεμεί επιβεβαίωση για', 'Confirmation pending for')}{' '}
            {props.bot.settings.leadNotifyEmailPending}.
          </p>
        ) : null}
        <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400 dark:text-slate-500">
          <li>
            {text(
              'Αποτυχία συγχρονισμού: φαίνεται στη βάση γνώσης και στο audit.',
              'Synchronization failure: shown in the knowledge base and audit log.',
            )}
          </li>
          <li>
            {text(
              'Προσέγγιση ορίου: φαίνεται στη Χρήση και χρεώσεις.',
              'Approaching a limit: shown in Usage and billing.',
            )}
          </li>
        </ul>
        {props.canWrite ? (
          <Button type="submit" disabled={pending}>
            <Icon name="save" className="size-4" />
            {pending ? text('Αποθήκευση…', 'Saving…') : text('Αποθήκευση', 'Save')}
          </Button>
        ) : null}
        {notice ? <Notice tone={notice.tone} text={notice.text} /> : null}
      </form>
    </Card>
  );
}

function PrivacyEditor(props: {
  siteId: string;
  bot: BotData;
  canWrite: boolean;
  onSaved: () => void;
}) {
  const { text } = useAdminLocale();
  const { retentionDayChoices } = useUiPrefs();
  const currentDays = props.bot.settings.historyRetentionDays;
  const days = retentionDayChoices.includes(currentDays)
    ? retentionDayChoices
    : [currentDays, ...retentionDayChoices];
  const [privacyUrl, setPrivacyUrl] = useState(props.bot.settings.privacyUrl ?? '');
  const [contactUrl, setContactUrl] = useState(props.bot.settings.contactUrl ?? '');
  const [noticeVersion, setNoticeVersion] = useState(props.bot.settings.noticeVersion);
  const [retention, setRetention] = useState(String(currentDays));
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [pending, setPending] = useState(false);

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setNotice(null);
    try {
      await readJson(`/v1/sites/${props.siteId}/bot`, {
        method: 'PATCH',
        body: JSON.stringify({
          settings: {
            privacyUrl: privacyUrl.trim() || null,
            contactUrl: contactUrl.trim() || null,
            noticeVersion: noticeVersion.trim(),
            historyRetentionDays: Number(retention),
          },
        }),
      });
      setNotice({ tone: 'ok', text: text('Το απόρρητο αποθηκεύτηκε.', 'Privacy saved.') });
      props.onSaved();
    } catch (error) {
      setNotice({
        tone: 'error',
        text: error instanceof Error ? error.message : text('Η αποθήκευση απέτυχε', 'Save failed'),
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <Card
      title={text('Απόρρητο', 'Privacy')}
      description={text(
        'Σύνδεσμος πολιτικής, έκδοση ενημέρωσης και διάρκεια διατήρησης συνομιλιών της ιστοσελίδας.',
        'Policy link, notice version, and website conversation retention period.',
      )}
    >
      <form onSubmit={(event) => void save(event)} className="grid gap-4 sm:grid-cols-2">
        <Field label={text('Σύνδεσμος πολιτικής απορρήτου', 'Privacy policy URL')}>
          <TextInput
            type="url"
            value={privacyUrl}
            disabled={!props.canWrite}
            onChange={(event) => setPrivacyUrl(event.target.value)}
            placeholder="https://"
          />
        </Field>
        <Field label={text('Σύνδεσμος επικοινωνίας', 'Contact URL')}>
          <TextInput
            type="url"
            value={contactUrl}
            disabled={!props.canWrite}
            onChange={(event) => setContactUrl(event.target.value)}
            placeholder="https://"
          />
        </Field>
        <Field
          label={text('Έκδοση ενημέρωσης', 'Notice version')}
          hint={text('Αποθηκεύεται μαζί με κάθε νέα επαφή.', 'Stored with each new contact.')}
        >
          <TextInput
            required
            value={noticeVersion}
            disabled={!props.canWrite}
            onChange={(event) => setNoticeVersion(event.target.value)}
          />
        </Field>
        <Field label={text('Διατήρηση συνομιλιών', 'Conversation retention')}>
          <Select
            className="mt-1.5"
            value={retention}
            disabled={!props.canWrite}
            onChange={setRetention}
            options={days.map((value) => ({
              value: String(value),
              label: text(`${value} ημέρες`, `${value} days`),
            }))}
          />
        </Field>
        <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 sm:col-span-2">
          {text(
            'Το κλειδί του παρόχου δεν εμφανίζεται στη διαχείριση. Η ενημέρωση τεχνητής νοημοσύνης μένει στο κείμενο του widget.',
            'The provider key is not shown in the admin UI. The AI notice remains in the widget copy.',
          )}
        </p>
        {props.canWrite ? (
          <div>
            <Button type="submit" disabled={pending}>
              <Icon name="save" className="size-4" />
              {pending ? text('Αποθήκευση…', 'Saving…') : text('Αποθήκευση', 'Save')}
            </Button>
          </div>
        ) : null}
        {notice ? (
          <div className="sm:col-span-2">
            <Notice tone={notice.tone} text={notice.text} />
          </div>
        ) : null}
      </form>
    </Card>
  );
}
